import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Modal from 'components/modal/modal-screen';
import StaticMap from 'components/static-map';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import { useCart } from 'hooks/use-cart';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import useRoutes from 'hooks/use-routes';
import { useOrderPayment } from 'pages/cart/payment/order-payment/use-order-payment';
import { logCartPlaceOrder } from 'pages/cart/payment/utils';
import { PlaceOrderButton } from 'pages/cart/place-order-button';
import { useCartContext } from 'state/cart';
import { actions, useAppDispatch } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { OrderStatus } from 'state/order';
import { ServiceMode } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { getUSState } from 'utils/restaurant';
import { routes } from 'utils/routing';

import { PaymentProcessLoading } from './components/payment-process-loading';
import { isCurbsideOrder } from './components/payment-process-loading/utils';
import {
  Address,
  Inner,
  LocationContainer,
  MapContainer,
  ModalContent,
  ModalHeading,
  StreetAddress,
  StyledLink,
  StyledPicture,
  Title,
} from './styled';

const serviceModeMessageMap = new Map<ServiceMode, string>([
  [ServiceMode.CATERING_DELIVERY, 'storeConfirmationCateringDelivery'],
  [ServiceMode.CATERING_PICKUP, 'storeConfirmationCateringPickup'],
  [ServiceMode.CURBSIDE, 'storeConfirmationCurbside'],
  [ServiceMode.DELIVERY, 'storeConfirmationDelivery'],
  [ServiceMode.DRIVE_THRU, 'storeConfirmationDriveThru'],
  [ServiceMode.EAT_IN, 'storeConfirmationEatIn'],
  [ServiceMode.TAKEOUT, 'storeConfirmationPickup'],
]);

const isSwitchingStoresWithinCart = (currentPathname: string) => {
  return currentPathname.includes(routes.cart);
};

const StoreConfirmationModal: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { goBack, navigate } = useNavigation();
  const { store } = useStoreContext();
  const { shouldConfirmStoreOnCheckout } = useUIContext();
  const { physicalAddress, restaurantImage } = store;
  const dispatch = useAppDispatch();
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const enableCheckoutAnimation = useFlag(LaunchDarklyFlag.ENABLE_CHECKOUT_ANIMATION);

  const streetAddress = physicalAddress?.address1 ?? '';
  const formattedCity = physicalAddress?.city ?? '';
  const state = getUSState(physicalAddress?.stateProvince ?? '');
  const postalCode = (physicalAddress?.postalCode ?? '').split('-')[0];
  const { formatMessage } = useIntl();
  const { getLocalizedRouteForPath } = useRoutes();
  const { logRBIEvent } = useMParticleContext();
  const { logNavigationClick } = useMParticleContext();
  const { setStoreLocatorCallbackUrl } = useLocationContext();
  const { pathname } = useRoute();
  const { serviceMode, serverOrder } = useCartContext();
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);
  const { cartEntries } = useCart();
  const isCurbside = isCurbsideOrder(store, serviceMode);

  const {
    commitOrder,
    isProcessingPayment,
    isErrorModalOpen,
    ErrorDialog,
    navigateToOrderConfirmation,
  } = useOrderPayment();

  const chooseDifferentLocation = useCallback(() => {
    if (isProcessingPayment) {
      return;
    }
    logNavigationClick(CustomEventNames.BUTTON_CLICK_OTHER_LOCATION);
    shouldConfirmStoreOnCheckout(false);
    const redirectUrl = isSwitchingStoresWithinCart(pathname) ? routes.cart : pathname;
    setStoreLocatorCallbackUrl(redirectUrl);
    navigate(getLocalizedRouteForPath('store-locator'));
  }, [
    isProcessingPayment,
    logNavigationClick,
    shouldConfirmStoreOnCheckout,
    pathname,
    setStoreLocatorCallbackUrl,
    navigate,
    getLocalizedRouteForPath,
  ]);

  const handleConfirm = () => {
    logCartPlaceOrder(logRBIEvent);
    // Set shouldRefetchOffers to true on order
    dispatch(actions.loyalty.setShouldRefetchOffers(true));
    commitOrder();
    setProcessingPayment(true);
  };

  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);

  let serviceModeMessage = serviceMode
    ? serviceModeMessageMap.get(serviceMode)
    : 'defaultOrderConfirmation';
  if (serviceMode === ServiceMode.TAKEOUT && isFastLaneEnabled) {
    serviceModeMessage = 'storeConfirmationFastLaneDriveThru';
  }

  const { top: topInset } = useSafeAreaInsets();
  const headerMarginTop = -topInset;

  // You should never be on this page if you have an empty cart.
  // Back button can do this! So lets just redirect to home if it happens.
  // hadCartEntries prevents us from redirecting off of confirm page right when cart empties on order (race condition on browser)
  const hadCartEntriesRef = useRef(!!cartEntries.length);
  let hadCartEntries = false;
  if (cartEntries.length || hadCartEntriesRef.current) {
    hadCartEntries = true;
    hadCartEntriesRef.current = true;
  }
  useEffect(() => {
    if (!hadCartEntries) {
      navigate(routes.base);
    }
  }, [navigate, hadCartEntries]);

  useEffect(() => {
    if (
      enableCheckoutAnimation &&
      serverOrder?.status === OrderStatus.PRICE_SUCCESSFUL &&
      isCurbside &&
      processingPayment
    ) {
      navigateToOrderConfirmation();
    }
  }, [
    enableCheckoutAnimation,
    isCurbside,
    navigateToOrderConfirmation,
    processingPayment,
    serverOrder?.status,
  ]);

  if (enableCheckoutAnimation && processingPayment && !isErrorModalOpen && !isCurbside) {
    return <PaymentProcessLoading rbiOrderId={serverOrder?.rbiOrderId} />;
  }

  return (
    <>
      <Modal
        mParticleEventData={{
          modalAppearanceEventMessage: 'Confirm store',
        }}
        allowsDismiss
        onDismiss={goBack}
        testID="store-confirmation-modal"
        accessibilityLabel="ModalContainer"
        backgroundColor={Styles.color.background}
        header={
          <ModalHeading _ios={{ marginTop: headerMarginTop }}>
            {restaurantImage ? (
              <StyledPicture
                image={restaurantImage}
                alt={store.name ?? 'Restaurant Name Missing'}
                // TODO: RN - Add testID to Picture props
                // testID="store-confirmation-modal-restaurant-image"
              />
            ) : (
              <MapContainer accessibilityLabel="MapContainer">
                <StaticMap
                  // TODO: RN - Add testID to StaticMap props
                  // testID="store-confirmation-modal-static-map"
                  initialZoomIOS={8000}
                  position={{
                    lat: store.latitude ?? 0,
                    lng: store.longitude ?? 0,
                  }}
                  title={store.name || store.physicalAddress?.address1}
                />
              </MapContainer>
            )}
          </ModalHeading>
        }
        {...(isDesktopWeb && { contentStyle: { margin: '20px', maxWidth: '800px' } })}
        showFixedHeader
      >
        <ModalContent>
          <Inner>
            <Title focusable>{formatMessage({ id: 'confirmYourStore' })}</Title>
            <LocationContainer marginBottom="$4">
              <StreetAddress nativeID="street-address" focusable>
                {formatMessage(
                  { id: serviceModeMessage },
                  {
                    location: streetAddress,
                  }
                )}
              </StreetAddress>
              <Address nativeID="city-state-zip-address" focusable>
                {formattedCity} {state} {postalCode}
              </Address>
            </LocationContainer>
            <PlaceOrderButton
              confirmation
              onPress={handleConfirm}
              isLoading={isProcessingPayment}
              isDisabled={isProcessingPayment}
            />
            <StyledLink onPress={chooseDifferentLocation}>
              {formatMessage({ id: 'confirmationDifferentLocation' })}
            </StyledLink>
          </Inner>
        </ModalContent>
        <ErrorDialog />
      </Modal>
    </>
  );
};

export default StoreConfirmationModal;
